"use strict";
import { createListenerMiddleware, createSlice } from "@reduxjs/toolkit";
import { createOnSetCapabilitiesByChainEffect } from "state/walletCapabilities/effects";
import {
  isAtomicBatchingSupported,
  isAtomicBatchingSupportedByChainId
} from "state/walletCapabilities/lib/handleGetCapabilities";
import { GetCapabilitiesStatus } from "state/walletCapabilities/types";
import { InterfaceEventNameLocal } from "features/telemetry/constants/interface";
import { sendAnalyticsEvent } from "features/telemetry/send.web";
import { InterfaceUserPropertyName, setUserProperty } from "features/telemetry/user";
import { hexToNumber } from "utils/hex";
const initialState = {
  getCapabilitiesStatus: GetCapabilitiesStatus.Unknown,
  byChain: {}
};
const walletCapabilitiesSlice = createSlice({
  name: "walletCapabilities",
  initialState,
  reducers: {
    setCapabilitiesNotSupported(state) {
      state.getCapabilitiesStatus = GetCapabilitiesStatus.Unsupported;
    },
    setCapabilitiesByChain(state, { payload }) {
      state.byChain = payload;
      state.getCapabilitiesStatus = GetCapabilitiesStatus.Supported;
    },
    handleResetWalletCapabilitiesState(state) {
      state.getCapabilitiesStatus = GetCapabilitiesStatus.Unknown;
      state.byChain = {};
    }
  }
});
export const { setCapabilitiesByChain, handleResetWalletCapabilitiesState, setCapabilitiesNotSupported } = walletCapabilitiesSlice.actions;
export default walletCapabilitiesSlice.reducer;
export const selectNeedsToCheckCapabilities = (state) => {
  return state.walletCapabilities.getCapabilitiesStatus === GetCapabilitiesStatus.Unknown;
};
const selectWalletCapabilitiesSupported = (state) => {
  return state.walletCapabilities.getCapabilitiesStatus === GetCapabilitiesStatus.Supported;
};
export const selectIsAtomicBatchingSupported = (state) => {
  const values = Object.values(state.walletCapabilities.byChain);
  return values.some(isAtomicBatchingSupported);
};
export const selectIsAtomicBatchingSupportedByChainId = (state) => {
  return (chainId) => {
    if (selectNeedsToCheckCapabilities(state)) {
      return void 0;
    }
    const areCapabilitiesSupported = selectWalletCapabilitiesSupported(state);
    if (!areCapabilitiesSupported) {
      return false;
    }
    const atomicBatchingSupported = isAtomicBatchingSupportedByChainId(state.walletCapabilities.byChain, chainId);
    return atomicBatchingSupported ?? false;
  };
};
export const walletCapabilitiesListenerMiddleware = createListenerMiddleware();
walletCapabilitiesListenerMiddleware.startListening({
  actionCreator: setCapabilitiesByChain,
  effect: (action, { getOriginalState }) => {
    createOnSetCapabilitiesByChainEffect({
      getOriginalState,
      onAtomicSupportedChainIdsDetected,
      onWalletCapabilitiesDetected
    })(action);
  }
});
function onAtomicSupportedChainIdsDetected(chainIds) {
  setUserProperty(InterfaceUserPropertyName.SupportsAtomicBatching, chainIds);
}
function onWalletCapabilitiesDetected(chainCapabilitiesResult) {
  for (const [chainId, capabilities] of Object.entries(chainCapabilitiesResult)) {
    sendAnalyticsEvent(InterfaceEventNameLocal.WalletCapabilitiesDetected, {
      chainId: hexToNumber(chainId),
      capabilities
    });
  }
}
